import React, { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { jobsEndpoint } from '../constants';

const STATUS = {
  PENDING: 'pending',
  PROCESSING: 'processing',
  SUCCESS: 'success',
  ERROR: 'error',
};

const StatusBadge = ({ status }) => {
  const config = {
    [STATUS.PENDING]:    { label: 'Queued',      cls: 'bg-gray-100 text-gray-600' },
    [STATUS.PROCESSING]: { label: 'Processing…', cls: 'bg-yellow-100 text-yellow-700' },
    [STATUS.SUCCESS]:    { label: 'Done',         cls: 'bg-green-100 text-green-700' },
    [STATUS.ERROR]:      { label: 'Failed',       cls: 'bg-red-100 text-red-700' },
  };
  const { label, cls } = config[status] || config[STATUS.PENDING];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>
      {status === STATUS.PROCESSING && (
        <span className="inline-block w-2 h-2 rounded-full bg-current animate-pulse" />
      )}
      {label}
    </span>
  );
};

// ── Share Modal ────────────────────────────────────────────────────────────────

const ShareModal = ({ successfulUploads, onConfirm, onSkip, sharing }) => {
  const [selected, setSelected] = useState(
    () => new Set(successfulUploads.map(u => u.applicant_id))
  );

  const toggle = (id) =>
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const toggleAll = () =>
    setSelected(
      selected.size === successfulUploads.length
        ? new Set()
        : new Set(successfulUploads.map(u => u.applicant_id))
    );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 overflow-hidden">

        {/* Header */}
        <div className="bg-gradient-to-r from-teal-500 to-teal-600 px-6 py-5 text-white">
          <div className="flex items-center gap-3 mb-1">
            <span className="text-2xl">🔗</span>
            <h2 className="text-lg font-bold">Share Candidate Profiles?</h2>
          </div>
          <p className="text-teal-100 text-sm">
            Shared profiles are visible to your team and collaborators.
          </p>
        </div>

        {/* Body */}
        <div className="px-6 py-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-medium text-gray-700">
              {successfulUploads.length} profile{successfulUploads.length !== 1 ? 's' : ''} created
            </p>
            <button
              onClick={toggleAll}
              className="text-xs text-teal-600 hover:text-teal-700 font-medium"
            >
              {selected.size === successfulUploads.length ? 'Deselect all' : 'Select all'}
            </button>
          </div>

          <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
            {successfulUploads.map((upload) => (
              <label
                key={upload.applicant_id}
                className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                  selected.has(upload.applicant_id)
                    ? 'border-teal-400 bg-teal-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <input
                  type="checkbox"
                  checked={selected.has(upload.applicant_id)}
                  onChange={() => toggle(upload.applicant_id)}
                  className="rounded border-gray-300 text-teal-500 focus:ring-teal-400"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">{upload.file_name}</p>
                  {upload.ai_evaluation?.score != null && (
                    <p className="text-xs text-gray-400">
                      AI Score: <span className="text-blue-600 font-semibold">{upload.ai_evaluation.score}/100</span>
                      {upload.ai_evaluation.rating && (
                        <span className="ml-2 text-purple-600">{upload.ai_evaluation.rating}</span>
                      )}
                    </p>
                  )}
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex gap-3">
          <button
            onClick={onSkip}
            disabled={sharing}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 text-sm disabled:opacity-50"
          >
            Skip
          </button>
          <button
            onClick={() => onConfirm(Array.from(selected))}
            disabled={sharing || selected.size === 0}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {sharing ? (
              <>
                <span className="inline-block w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin" />
                Sharing…
              </>
            ) : (
              `Share ${selected.size > 0 ? `${selected.size} ` : ''}Profile${selected.size !== 1 ? 's' : ''}`
            )}
          </button>
        </div>

      </div>
    </div>
  );
};

// ── Main Component ─────────────────────────────────────────────────────────────

const CreateCandidate = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const dropRef = useRef(null);

  const [files, setFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [globalError, setGlobalError] = useState(null);
  const [done, setDone] = useState(false);

  // Share modal state
  const [showShareModal, setShowShareModal] = useState(false);
  const [successfulUploads, setSuccessfulUploads] = useState([]);
  const [sharing, setSharing] = useState(false);
  const [shareResult, setShareResult] = useState(null); // { shared: n, skipped: bool }

  // ── File helpers ─────────────────────────────────────────────────────────────

  const addFiles = useCallback((incoming) => {
    const accepted = incoming.filter(f => /\.(pdf|doc|docx)$/i.test(f.name));
    if (!accepted.length) return;
    setFiles(prev => {
      const existing = new Set(prev.map(e => e.file.name));
      return [
        ...prev,
        ...accepted
          .filter(f => !existing.has(f.name))
          .map(f => ({
            id: `${f.name}-${f.lastModified}`,
            file: f,
            status: STATUS.PENDING,
            result: null,
            error: null,
          })),
      ];
    });
  }, []);

  const removeFile = (id) => setFiles(prev => prev.filter(f => f.id !== id));

  const updateFile = (id, patch) =>
    setFiles(prev => prev.map(f => f.id === id ? { ...f, ...patch } : f));

  // ── Drag & drop ──────────────────────────────────────────────────────────────

  const onDragOver  = (e) => { e.preventDefault(); setIsDragging(true); };
  const onDragLeave = (e) => { if (!dropRef.current?.contains(e.relatedTarget)) setIsDragging(false); };
  const onDrop      = (e) => { e.preventDefault(); setIsDragging(false); addFiles(Array.from(e.dataTransfer.files)); };
  const onInputChange = (e) => addFiles(Array.from(e.target.files));

  // ── Upload ───────────────────────────────────────────────────────────────────

  const runUpload = async (entriesToProcess) => {
    if (!entriesToProcess.length) return;

    entriesToProcess.forEach(e => updateFile(e.id, { status: STATUS.PROCESSING, error: null, result: null }));

    const formData = new FormData();
    formData.append('file_type', 'cv');
    entriesToProcess.forEach(e => formData.append('files', e.file));

    try {
      const res = await axios.post(`${jobsEndpoint}bulk_upload_and_create_cv/`, formData, {
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (evt) => {
          if (evt.total) setUploadProgress(Math.round((evt.loaded / evt.total) * 100));
        },
      });

      if (res.status === 403 && res.data?.error === 'limit_reached') {
        navigate('/limit-reached', { state: { resource: res.data.resource || 'candidates' } });
        return;
      }

      const { results = [], failed = [] } = res.data;

      const nameToResult = {};
      results.forEach(r => { nameToResult[r.file_name] = r; });

      const nameToFailed = {};
      failed.forEach(f => { nameToFailed[f.file_name] = f.error; });

      const newSuccesses = [];

      entriesToProcess.forEach(entry => {
        if (nameToResult[entry.file.name]) {
          const result = nameToResult[entry.file.name];
          updateFile(entry.id, { status: STATUS.SUCCESS, result });
          // Collect for share modal — requires applicant_id in the response
          if (result.applicant_id) {
            newSuccesses.push(result);
          }
        } else if (nameToFailed[entry.file.name]) {
          updateFile(entry.id, { status: STATUS.ERROR, error: nameToFailed[entry.file.name] });
        } else {
          updateFile(entry.id, { status: STATUS.ERROR, error: 'No response from server for this file' });
        }
      });

      // Show share modal if we have shareable profiles
      if (newSuccesses.length > 0) {
        setSuccessfulUploads(newSuccesses);
        setShowShareModal(true);
      }

    } catch (err) {
      if (err.response?.status === 403 && err.response?.data?.error === 'limit_reached') {
        navigate('/limit-reached', { state: { resource: err.response.data.resource || 'candidates' } });
        return;
      }
      const msg = err.response?.data?.error || err.message;
      setGlobalError(`Upload failed — ${msg}`);
      entriesToProcess.forEach(e => updateFile(e.id, { status: STATUS.ERROR, error: msg }));
    }
  };

  const handleSubmit = async () => {
    const pending = files.filter(f => f.status === STATUS.PENDING);
    if (!pending.length) return;
    setSubmitting(true);
    setGlobalError(null);
    setUploadProgress(0);
    setShareResult(null);
    await runUpload(pending);
    setSubmitting(false);
    setDone(true);
  };

  const retryFailed = async () => {
    const failed = files.filter(f => f.status === STATUS.ERROR);
    if (!failed.length) return;
    setSubmitting(true);
    setGlobalError(null);
    setUploadProgress(0);
    await runUpload(failed);
    setSubmitting(false);
  };

  const resetAll = () => {
    setFiles([]);
    setDone(false);
    setGlobalError(null);
    setUploadProgress(0);
    setShareResult(null);
    setSuccessfulUploads([]);
  };

  // ── Share handlers ────────────────────────────────────────────────────────────

  const handleConfirmShare = async (selectedIds) => {
    if (!selectedIds.length) {
      setShowShareModal(false);
      return;
    }
    setSharing(true);
    try {
      await Promise.all(
        selectedIds.map(id =>
          axios.post(
            `${jobsEndpoint}applicants/${id}/toggle-shared/`,
            { is_shared: true },
            { headers: { Authorization: `Token ${token}` } }
          )
        )
      );
      setShareResult({ shared: selectedIds.length });
    } catch (err) {
      setGlobalError('Some profiles could not be shared. You can update them from the applicant list.');
    } finally {
      setSharing(false);
      setShowShareModal(false);
    }
  };

  const handleSkipShare = () => {
    setShowShareModal(false);
    setShareResult({ skipped: true });
  };

  // ── Derived ───────────────────────────────────────────────────────────────────

  const successCount = files.filter(f => f.status === STATUS.SUCCESS).length;
  const errorCount   = files.filter(f => f.status === STATUS.ERROR).length;
  const pendingCount = files.filter(f => f.status === STATUS.PENDING).length;

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <>
      {/* Share Modal */}
      {showShareModal && (
        <ShareModal
          successfulUploads={successfulUploads}
          onConfirm={handleConfirmShare}
          onSkip={handleSkipShare}
          sharing={sharing}
        />
      )}

      <div className="max-w-2xl mx-auto p-6">
        <div className="bg-white rounded-xl shadow border p-6">
          <h1 className="text-2xl font-bold text-gray-800 mb-1">Bulk CV Upload</h1>
          <p className="text-gray-500 text-sm mb-6">
            Drop multiple CVs — profiles and AI evaluations are created automatically.
          </p>

          {globalError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 text-sm text-red-700">
              {globalError}
            </div>
          )}

          {/* Share result banner */}
          {shareResult && !showShareModal && (
            <div className="bg-teal-50 border border-teal-200 rounded-lg p-3 mb-4 text-sm text-teal-800 flex items-center gap-2">
              <span>🔗</span>
              {shareResult.skipped
                ? 'Profiles not shared — you can share them anytime from the applicant list.'
                : `${shareResult.shared} profile${shareResult.shared !== 1 ? 's' : ''} shared successfully.`}
            </div>
          )}

          {/* Drop zone */}
          <div
            ref={dropRef}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            onClick={() => document.getElementById('bulk-cv-input').click()}
            className={`border-2 border-dashed rounded-xl p-10 text-center transition-all duration-200 cursor-pointer
              ${isDragging ? 'border-blue-500 bg-blue-50 scale-[1.01]' : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'}`}
          >
            <input id="bulk-cv-input" type="file" multiple accept=".pdf,.doc,.docx" onChange={onInputChange} className="hidden" />
            <div className="text-4xl mb-3">{isDragging ? '📂' : '📄'}</div>
            <p className="font-semibold text-gray-700">{isDragging ? 'Drop your CVs here' : 'Drag & drop CVs here'}</p>
            <p className="text-xs text-gray-400 mt-1">or click to browse — PDF, DOC, DOCX · max 10 MB each</p>
          </div>

          {/* Progress bar */}
          {submitting && uploadProgress > 0 && uploadProgress < 100 && (
            <div className="mt-4">
              <div className="flex justify-between text-xs text-gray-500 mb-1">
                <span>Uploading…</span><span>{uploadProgress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-1.5">
                <div className="bg-blue-500 h-1.5 rounded-full transition-all duration-200" style={{ width: `${uploadProgress}%` }} />
              </div>
            </div>
          )}

          {/* File list */}
          {files.length > 0 && (
            <div className="mt-5 space-y-2">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-gray-700">{files.length} file{files.length !== 1 ? 's' : ''}</p>
                {!submitting && <button onClick={resetAll} className="text-xs text-gray-400 hover:text-red-500">Clear all</button>}
              </div>

              {files.map(({ id, file, status, result, error }) => (
                <div
                  key={id}
                  className={`flex items-start gap-3 rounded-lg border px-4 py-3 text-sm transition-colors
                    ${status === STATUS.SUCCESS ? 'border-green-200 bg-green-50'
                    : status === STATUS.ERROR   ? 'border-red-200 bg-red-50'
                    : 'border-gray-200 bg-gray-50'}`}
                >
                  <span className="text-lg mt-0.5 shrink-0">
                    {status === STATUS.SUCCESS ? '✅' : status === STATUS.ERROR ? '❌' : '📄'}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-gray-800 truncate">{file.name}</span>
                      <span className="text-xs text-gray-400">{(file.size / 1024 / 1024).toFixed(2)} MB</span>
                      <StatusBadge status={status} />
                    </div>
                    {status === STATUS.SUCCESS && result?.ai_evaluation?.completed && (
                      <div className="mt-1.5 flex items-center gap-3 flex-wrap">
                        {result.ai_evaluation.score != null && (
                          <span className="text-xs font-semibold text-blue-700">Score: {result.ai_evaluation.score}/100</span>
                        )}
                        {result.ai_evaluation.rating && (
                          <span className="text-xs text-purple-700">{result.ai_evaluation.rating}</span>
                        )}
                        {result.applicant_id && shareResult?.shared && (
                          <span className="text-xs text-teal-600 font-medium">🔗 Shared</span>
                        )}
                      </div>
                    )}
                    {status === STATUS.ERROR && error && (
                      <p className="text-xs text-red-600 mt-1">{error}</p>
                    )}
                  </div>
                  {status === STATUS.PENDING && !submitting && (
                    <button onClick={() => removeFile(id)} className="text-gray-300 hover:text-red-400 text-base leading-none shrink-0 mt-0.5">✕</button>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Done banner */}
          {done && !submitting && !showShareModal && (
            <div className={`mt-4 rounded-lg p-3 text-sm text-center font-medium flex items-center justify-center gap-3
              ${errorCount === 0 ? 'bg-green-50 text-green-800' : 'bg-yellow-50 text-yellow-800'}`}>
              <span>
                {successCount > 0 && `${successCount} profile${successCount !== 1 ? 's' : ''} created. `}
                {errorCount > 0 && `${errorCount} file${errorCount !== 1 ? 's' : ''} failed.`}
              </span>
              {errorCount > 0 && (
                <button onClick={retryFailed} className="text-xs px-3 py-1 rounded bg-yellow-200 hover:bg-yellow-300 text-yellow-900 font-semibold">
                  Retry failed
                </button>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 mt-6">
            <button type="button" onClick={() => window.history.back()} disabled={submitting}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 text-sm">
              Cancel
            </button>

            {done ? (
              <>
                <button onClick={resetAll} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm">Upload More</button>
                <button onClick={() => navigate('/dashboard')} className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-800 text-sm">Dashboard</button>
              </>
            ) : (
              <button type="button" onClick={handleSubmit} disabled={submitting || pendingCount === 0}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium">
                {submitting ? (
                  <>
                    <span className="inline-block w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin" />
                    {uploadProgress > 0 && uploadProgress < 100 ? `Uploading ${uploadProgress}%…` : 'Creating profiles…'}
                  </>
                ) : (
                  `Upload & Create ${pendingCount > 0 ? `${pendingCount} CV Profile${pendingCount !== 1 ? 's' : ''}` : ''}`
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default CreateCandidate;